/**
 * Frontend telemetry helper. Fire-and-forget — never throws, never blocks UI.
 *
 *  - Session start fires once after auth.
 *  - Session heartbeat fires every 60s while the tab is visible.
 *  - Loadout snapshot fires once per session (after first state fetch).
 *  - Client errors fire from window.onerror / unhandledrejection.
 */

const HEARTBEAT_INTERVAL_MS = 60 * 1000;

let authHeader: string | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let currentScreen = 'boot';
let sessionStartFired = false;
let loadoutSnapshotFired = false;

export function setTelemetryAuth(token: string): void {
  authHeader = `Bearer ${token}`;
}

export function setCurrentScreen(screen: string): void {
  currentScreen = screen;
}

async function post(path: string, body: Record<string, unknown>): Promise<void> {
  if (!authHeader) return;
  try {
    await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: authHeader },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // swallow — telemetry must never propagate
  }
}

export function startSession(): void {
  if (sessionStartFired) return;
  sessionStartFired = true;
  void post('/api/telemetry/session-start', {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    platform: typeof navigator !== 'undefined' ? navigator.platform : undefined,
  });
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = setInterval(() => {
    if (document.visibilityState === 'visible') {
      void post('/api/telemetry/session-heartbeat', { screen: currentScreen });
    }
  }, HEARTBEAT_INTERVAL_MS);
}

export function snapshotLoadout(): void {
  if (loadoutSnapshotFired) return;
  loadoutSnapshotFired = true;
  void post('/api/telemetry/loadout-snapshot', {});
}

export function reportClientError(message: string, stack?: string): void {
  void post('/api/telemetry/client-error', {
    message,
    stack,
    screen: currentScreen,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  });
}

export function reportFightSentiment(encounterId: string, rating: 'easy' | 'normal' | 'hard'): void {
  void post('/api/telemetry/fight-sentiment', { encounterId, rating });
}

export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (ev) => {
    reportClientError(ev.message || 'Unknown error', ev.error?.stack);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportClientError(`unhandledrejection: ${message}`, stack);
  });
}
